import { useEffect, useRef, useState } from "react";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwButton from "../../shared/OwButton/OwButton";
import IOwTableCols from "../../../models/IOwTableCols";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IPromo_Code from "../../../models/IPromo_Code";
import IGetPromoCodesDTO from "../../../models/DTO/IGetPromoCodesDTO";
import IOnGetPromoCodesDTO from "../../../models/DTO/IOnGetPromoCodesDTO";
import IProcessPromoCodeDTO from "../../../models/DTO/IProcessPromoCodeDTO";
import IOnProcessPromoCodeDTO from "../../../models/DTO/IOnProcessPromoCodeDTO";
import { PromoCodeSidebar } from "../PromoCodeSidebar/PromoCodeSidebar";
import UserService from "../../../services/UserService";
import PromoCodeService from "../../../services/PromoCodeService";
import HelperService from "../../../services/HelperService";
import CurrenciesEnum from "../../../models/CurrenciesEnum";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import { OwFnTable } from "../../shared/OwFnTable/OwFnTable";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwLabel from "../../shared/OwLabel/OwLabel";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import { useAppContextNonNull } from "@contexts/AppContext";
import "./PromoCodes.scss";

const SEARCH_DEBOUNCE_MS = 300;

/** Supplier-admin list + CRUD entry point for promo codes (gated by Suppliers.has_promo_codes). */
export const PromoCodes = () => {
    const { userModel, supplierModel, uiContext, socket } = useAppContextNonNull();

    const [dataSet, setDataSet] = useState<IPromo_Code[]>([]);
    const [totalPromoCodes, setTotalPromoCodes] = useState(0);
    const [isDataFetching, setIsDataFetching] = useState(true);
    const [isInitialData, setIsInitialData] = useState(true);
    const [pagination, setPagination] = useState({ offset: 0, limit: 10 });
    const [search, setSearch] = useState("");
    const [sourceDeleteElem, setSourceDeleteElem] = useState<string | null>(null);
    const [rowToDelete, setRowToDelete] = useState<IPromo_Code | null>(null);

    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const getPromoCodes = (opts?: { offset?: number; limit?: number; search?: string }) => {
        const payload: IGetPromoCodesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            limit: opts?.limit ?? pagination.limit,
            offset: opts?.offset ?? pagination.offset,
        };

        const searchValue = (opts?.search ?? search).trim();

        if (searchValue.length) {
            payload.search = searchValue;
        }

        socket!.emit(EndpointsEnum.PromoCodesGetPromoCodes, payload);

        setIsDataFetching(true);
    };

    const onGetPromoCodes = (data: IOnGetPromoCodesDTO) => {
        setDataSet(data.promoCodes || []);
        setTotalPromoCodes(data.totalPromoCodes || 0);
        setIsInitialData(false);
        setIsDataFetching(false);
    };

    // The socket handlers are registered once (empty-deps effect), so they would otherwise close over
    // the first render's getPromoCodes and refetch page 1 unfiltered after a delete / save. The ref
    // always points at the latest closure, which reads the current pagination and search.
    const getPromoCodesRef = useRef(getPromoCodes);
    getPromoCodesRef.current = getPromoCodes;

    const onProcessPromoCode = (_data: IOnProcessPromoCodeDTO) => {
        getPromoCodesRef.current();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.PromoCodesOnGetPromoCodes, fn: onGetPromoCodes },
        { endpoint: EndpointsEnum.PromoCodesOnProcessPromoCode, fn: onProcessPromoCode },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket!.on(_socket.endpoint, _socket.fn);
        }

        getPromoCodes();

        return () => {
            for (const _socket of sockets) {
                socket!.off(_socket.endpoint, _socket.fn);
            }

            if (searchTimer.current) {
                clearTimeout(searchTimer.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const paginationChanged = (offset: number, limit: number) => {
        setPagination({ offset, limit });
        getPromoCodes({ offset, limit });
    };

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        setSearch(value);

        if (searchTimer.current) {
            clearTimeout(searchTimer.current);
        }

        searchTimer.current = setTimeout(() => {
            setPagination((current) => ({ ...current, offset: 0 }));
            getPromoCodes({ offset: 0, search: value });
        }, SEARCH_DEBOUNCE_MS);
    };

    const confirmDelete = (row: IPromo_Code, index: number) => {
        setSourceDeleteElem(`row-${index}`);
        setRowToDelete(row);
    };

    const cancelDelete = () => {
        setSourceDeleteElem(null);
        setRowToDelete(null);
    };

    const commitDelete = () => {
        if (rowToDelete) {
            const payload: IProcessPromoCodeDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                promoCode: { id: rowToDelete.id, guid: rowToDelete.guid },
                deleteFlag: true,
            };

            socket!.emit(EndpointsEnum.PromoCodesProcessPromoCode, payload);

            setRowToDelete(null);
            setSourceDeleteElem(null);
        }
    };

    const openSidebar = (promoCode?: IPromo_Code) => {
        const sidebarComponent = (
            <PromoCodeSidebar
                socket={socket!}
                promoCode={promoCode}
                setActivityIndicator={uiContext.setActivityIndicator}
                notifyFn={uiContext.notifyFn}
                closeFn={closeSidebar}
            />
        );

        uiContext.setSidebarComponent(sidebarComponent);
        uiContext.toggleSidebar(true);
    };

    const closeSidebar = (refreshData?: boolean) => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        if (refreshData) {
            getPromoCodes();
        }
    };

    const renderActions = (row: IPromo_Code, index: number) => {
        return (
            <div>
                <OwDeleteEditActions
                    isEditMode={false}
                    onTrashClickFn={() => {
                        confirmDelete(row, index);
                    }}
                    onEditClickFn={() => {
                        openSidebar(row);
                    }}
                />
            </div>
        );
    };

    const renderBoolean = (value: boolean) => {
        return value ? <i className={"fas fa-check text-success"} /> : <i className={"fas fa-times text-danger"} />;
    };

    const supplierCurrency = (supplierModel.currency_id ? CurrenciesEnum[supplierModel.currency_id] : undefined) || "USD";

    const renderMoney = (value: number | null | undefined) => {
        if (value === null || value === undefined) {
            return "";
        }

        return HelperService.formatCurrency({
            value: Number(value),
            precision: supplierModel.pricing_precision || 2,
            currency: supplierCurrency,
        });
    };

    // Calendar dates ('YYYY-MM-DD'): format as-is. parseDate converts to the user's timezone and shows the day before in the US.
    const renderDate = (row: IPromo_Code, prop: "start_date" | "end_date") => {
        return row[prop] ? UserService.parseUTCDate(row[prop]) : null;
    };

    const columns: IOwTableCols[] = [
        {
            title: "",
            valueProp: "",
            renderFn: renderActions,
            renderFnEmitsRow: true,
            className: "w-5r",
        },
        { title: "name", valueProp: "name" },
        {
            title: "code",
            valueProp: "code",
            renderFnEmitsRow: true,
            renderFn: (row: IPromo_Code) => <span className={"promo-code-cell"}>{row.code}</span>,
        },
        {
            title: "active",
            valueProp: "active",
            renderFnEmitsRow: true,
            renderFn: (row: IPromo_Code) => renderBoolean(!!row.active),
        },
        {
            title: "discount",
            valueProp: "discount_value",
            renderFnEmitsRow: true,
            renderFn: (row: IPromo_Code) =>
                PromoCodeService.formatDiscountLabel(row.discount_type_id, row.discount_value, supplierModel.pricing_precision || 2),
        },
        {
            title: "min_order_subtotal",
            valueProp: "min_order_subtotal",
            renderFnEmitsRow: true,
            renderFn: (row: IPromo_Code) => renderMoney(row.min_order_subtotal),
        },
        {
            title: "max_uses",
            valueProp: "max_uses",
            renderFnEmitsRow: true,
            renderFn: (row: IPromo_Code) =>
                row.max_uses === null || row.max_uses === undefined ? <OwLabel title={"unlimited"} noBottomMargin={true} /> : row.max_uses,
        },
        {
            title: "times_used",
            valueProp: "times_used",
            renderFnEmitsRow: true,
            renderFn: (row: IPromo_Code) => row.times_used || 0,
        },
        {
            title: "single_use_per_customer",
            valueProp: "single_use_per_customer",
            renderFnEmitsRow: true,
            renderFn: (row: IPromo_Code) => renderBoolean(!!row.single_use_per_customer),
        },
        {
            title: "start_date",
            valueProp: "start_date",
            renderFnEmitsRow: true,
            renderFn: (row: IPromo_Code) => renderDate(row, "start_date"),
        },
        {
            title: "end_date",
            valueProp: "end_date",
            renderFnEmitsRow: true,
            renderFn: (row: IPromo_Code) => renderDate(row, "end_date"),
        },
    ];

    return (
        <div className={"PromoCodes position-relative"}>
            <div className="row">
                <div className={"col-12 d-flex align-items-center justify-items-center"}>
                    <OwTitle className={"mt-3 me-3"} leftIconName={"fad fa-ticket"} title={"promo_codes"} />
                    <div>
                        <OwButton
                            title={"create"}
                            leftIconClass={"fas fa-plus"}
                            className={"btn-warning"}
                            testId={"promo-codes-create"}
                            fn={() => {
                                openSidebar();
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className={"row mt-2"}>
                <div className={"col-12 col-md-4"}>
                    <OwFnInput name={"search"} placeholder={"search"} value={search} fn={onSearchChange} testId={"promo-codes-search"} />
                </div>
            </div>

            <div className={"row mt-3"}>
                <div className="col-12 position-relative">
                    <ConfirmDeleteOverlayFn
                        targetElem={sourceDeleteElem}
                        handleConfirm={commitDelete}
                        handleCancel={cancelDelete}
                        leftAdjustment={-12}
                        skipMainView={true}
                        nestedParentElem={"promo-codes-table"}
                    />

                    <OwFnTable
                        id={"promo-codes-table"}
                        columns={columns}
                        dataSet={dataSet}
                        dataFetch={isDataFetching}
                        initialData={isInitialData}
                        totalRows={totalPromoCodes}
                        tableConfig={{ tableStriped: true, tableHover: true }}
                    />
                </div>
            </div>

            <div className={"row"}>
                <div className={"col-12"}>
                    <PaginationBar
                        offset={pagination.offset}
                        limit={pagination.limit}
                        totalRecords={totalPromoCodes}
                        onPaginationChanged={paginationChanged}
                    />
                </div>
            </div>
        </div>
    );
};
